import React, { useState, useEffect } from 'react';
import { checkGmailStatus, disconnectGmail } from '../../services/emailService';
import './EmailSettings.css';

const EmailSettings: React.FC = () => {
  const [gmailConnected, setGmailConnected] = useState<boolean>(false);
  const [gmailEmail, setGmailEmail] = useState<string>('');
  const [loading, setLoading] = useState<boolean>(true);
  const [disconnecting, setDisconnecting] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);

  useEffect(() => {
    // Check Gmail connection status
    const checkGmail = async () => {
      try {
        setLoading(true);
        const status = await checkGmailStatus();
        console.log('Gmail status response:', status);
        setGmailConnected(status.connected);
        if (status.email) {
          setGmailEmail(status.email);
        }
      } catch (err) {
        console.error('Error checking Gmail status:', err);
        setError('Failed to check Gmail connection status');
      } finally {
        setLoading(false);
      }
    };
    
    // Check for Gmail connection success from redirect
    const urlParams = new URLSearchParams(window.location.search);
    if (urlParams.get('gmail_connected') === 'true') {
      setSuccess('Gmail connected successfully!');
      setGmailConnected(true);
      const gmailEmail = urlParams.get('gmail_email');
      if (gmailEmail) {
        setGmailEmail(gmailEmail);
      }
      
      // Remove query parameters but keep the path
      window.history.replaceState({}, document.title, window.location.pathname);
    }
    
    // Check Gmail status
    checkGmail();
    
    // Set up interval to periodically check Gmail status
    const intervalId = setInterval(checkGmail, 5000);
    
    // Clean up interval on component unmount
    return () => clearInterval(intervalId);
  }, []);

  const handleConnectGmail = () => {
    // Use the full API URL without modification since the route is already prefixed with /api
    const apiBaseUrl = (process.env.REACT_APP_API_URL || 'http://localhost:5004/api').replace(/\/api$/, '');
    
    // Get the authentication token from localStorage
    const token = localStorage.getItem('token');
    
    // Include the token in the URL as a query parameter
    window.location.href = `${apiBaseUrl}/api/auth/gmail?auth_token=${token}`;
  };

  const handleDisconnectGmail = async () => {
    try {
      setDisconnecting(true);
      console.log('Disconnecting Gmail...');
      const result = await disconnectGmail();
      console.log('Disconnect result:', result);
      
      // Force status refresh
      const status = await checkGmailStatus();
      console.log('Gmail status after disconnect:', status);
      
      setGmailConnected(false);
      setGmailEmail('');
      setSuccess('Gmail disconnected successfully');
      
      // Force page reload to clear any cached state
      window.location.reload();
    } catch (err) {
      console.error('Error disconnecting Gmail:', err);
      setError('Failed to disconnect Gmail');
    } finally {
      setDisconnecting(false);
    }
  };

  return (
    <div className="email-settings">
      <h2>Email Settings</h2>
      
      {error && (
        <div className="alert alert-danger alert-dismissible fade show" role="alert">
          {error}
          <button 
            type="button" 
            className="btn-close" 
            data-bs-dismiss="alert" 
            aria-label="Close"
            onClick={() => setError(null)}
          ></button>
        </div>
      )}
      
      {success && (
        <div className="alert alert-success alert-dismissible fade show" role="alert">
          {success}
          <button 
            type="button" 
            className="btn-close" 
            data-bs-dismiss="alert" 
            aria-label="Close"
            onClick={() => setSuccess(null)}
          ></button>
        </div>
      )}
      
      <div className="card mb-4">
        <div className="card-header">
          <h3 className="card-title">Email Providers</h3>
        </div>
        <div className="card-body">
          <div className="row">
            <div className="col-md-6">
              <div className="provider-card">
                <div className="provider-header">
                  <img src="/images/sendgrid-logo.png" alt="SendGrid" className="provider-logo" />
                  <h4>SendGrid</h4>
                </div>
                <div className="provider-status">
                  <span className="badge bg-success">Always Available</span>
                </div>
                <p className="provider-description">
                  Our default email provider with tracking capabilities. No setup required.
                </p>
              </div>
            </div>
            
            <div className="col-md-6">
              <div className="provider-card">
                <div className="provider-header">
                  <img src="/images/gmail-logo.png" alt="Gmail" className="provider-logo" />
                  <h4>Gmail</h4>
                </div>
                <div className="provider-status">
                  {loading ? (
                    <div className="spinner-border spinner-border-sm text-primary" role="status">
                      <span className="visually-hidden">Loading...</span>
                    </div>
                  ) : gmailConnected ? (
                    <span className="badge bg-success">Connected</span>
                  ) : (
                    <span className="badge bg-secondary">Not Connected</span>
                  )}
                </div>
                {gmailConnected && gmailEmail && (
                  <p className="provider-email">{gmailEmail}</p>
                )}
                <p className="provider-description">
                  Send emails directly from your Gmail account. Requires Google authentication.
                </p>
                <div className="provider-actions">
                  {gmailConnected ? (
                    <div>
                      <button 
                        className="btn btn-outline-danger mb-2" 
                        onClick={handleDisconnectGmail}
                        disabled={disconnecting}
                      >
                        {disconnecting ? (
                          <>
                            <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
                            Disconnecting...
                          </>
                        ) : (
                          <>
                            <i className="bi bi-x-circle me-2"></i>
                            Disconnect Gmail
                          </>
                        )}
                      </button>
                      
                      {/* Direct disconnect link for testing */}
                      <div className="mt-2">
                        <a 
                          href={(process.env.REACT_APP_API_URL || 'http://localhost:5004/api').replace(/\/api$/, '') + '/api/gmail/disconnect'}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="small text-muted"
                        >
                          Direct disconnect link
                        </a>
                      </div>
                    </div>
                  ) : (
                    <div>
                      <button 
                        className="btn btn-primary mb-2" 
                        onClick={handleConnectGmail}
                        disabled={loading}
                      >
                        <i className="bi bi-google me-2"></i>
                        Connect Gmail
                      </button>
                      
                      {/* Direct connect links for testing */}
                      <div className="mt-2">
                        <div>
                          <a 
                            href="http://localhost:5004/api/direct-mock-connect"
                            className="small text-muted"
                          >
                            Direct connect link (mock)
                          </a>
                        </div>
                        <div className="mt-1">
                          <a 
                            href="http://localhost:5004/api/force-connect?email=user@gmail.com"
                            className="small text-muted"
                          >
                            Force connect (bypass all)
                          </a>
                        </div>
                      </div>
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
      
      <div className="card">
        <div className="card-header">
          <h3 className="card-title">Email Preferences</h3>
        </div>
        <div className="card-body">
          <form>
            <div className="mb-3">
              <label className="form-label">Default Email Provider</label>
              <select className="form-select">
                <option value="sendgrid">SendGrid (System Default)</option>
                <option value="gmail" disabled={!gmailConnected}>
                  Gmail {!gmailConnected && '(Connect Gmail first)'}
                </option>
              </select>
            </div>
            
            <div className="mb-3">
              <div className="form-check form-switch">
                <input className="form-check-input" type="checkbox" id="trackEmails" defaultChecked />
                <label className="form-check-label" htmlFor="trackEmails">
                  Track email opens and responses
                </label>
              </div>
              <small className="form-text text-muted">
                This helps you see when professors view your emails and track response rates.
              </small>
            </div>
            
            <div className="mb-3">
              <div className="form-check form-switch">
                <input className="form-check-input" type="checkbox" id="emailNotifications" defaultChecked />
                <label className="form-check-label" htmlFor="emailNotifications">
                  Receive email notifications when professors respond
                </label>
              </div>
            </div>
            
            <button type="submit" className="btn btn-primary">
              Save Preferences
            </button>
          </form>
        </div>
      </div>
    </div>
  );
};

export default EmailSettings;
